import { forwardRef } from 'react'
import { Avatar } from '@/components/ui/avatar'
import { Separator } from '@/components/ui/separator'
import { useGetCountries } from '@/features/experience-shell/queries/use-get-countries'
import type { RemapExperienceForViewOnly } from '@/features/experience-view-only/types'
import { useTranslation } from '@/translations/i18n'
import { AnswerField, Content, YesNoAnswerField } from './atoms'

export const BackgroundStep = forwardRef<
	HTMLDivElement,
	{ experience: RemapExperienceForViewOnly }
>(({ experience }, ref) => {
	const { t } = useTranslation()
	const countriesQuery = useGetCountries()
	const countryName = countriesQuery.data?.data.find(
		(country) =>
			country.code ===
			experience.expertise.answers.residential_country_region?.answer
	)?.name
	return (
		<div className='flex w-full flex-col' ref={ref}>
			<Content margin={6} title={t('experience_shell:step_background')}>
				<Avatar
					imageUrl={experience.host.profilePhotoUrl || ''}
					name={experience.host.firstName ?? '-'}
					size={'lg'}
				/>

				<AnswerField
					hasValue={
						!!experience.expertise.answers.expertise_years?.answer
					}
					labelTitle={t('experience_shell:years_experience')}
					value={experience.expertise.answers.expertise_years?.answer}
				/>

				<AnswerField
					hasValue={
						!!experience.expertise.answers.online_profile_links
							?.answer &&
						!!experience.expertise.answers.online_profile_links
							?.answer.length
					}
					labelTitle={t('experience_shell:online_profiles')}
				>
					{experience.expertise.answers.online_profile_links?.answer.map(
						(item, index) => (
							<li key={`${index}-${item}`} title={item}>
								{item}
							</li>
						)
					)}
				</AnswerField>

				<AnswerField
					hasValue={!!experience.host.airbnbVerifiedPhone}
					labelTitle={t('experience_view_only:phone_number')}
					value={
						experience.host.airbnbVerifiedPhone
							? `+${experience.host.airbnbVerifiedPhone}`
							: undefined
					}
				/>
			</Content>

			<Separator orientation='horizontal' />

			<Content margin={6}>
				<AnswerField
					hasValue={!!countryName}
					labelTitle={t('experience_shell:country_region')}
					value={countryName}
				/>
				<AnswerField
					hasValue={
						!!experience.expertise.answers.residential_address
							?.answer
					}
					labelTitle={t('experience_shell:street_address')}
					value={
						experience.expertise.answers.residential_address?.answer
					}
				/>
				<AnswerField
					hasValue={
						!!experience.expertise.answers
							.residential_address_extras?.answer
					}
					labelTitle={t('experience_shell:apt_suite')}
					value={
						experience.expertise.answers.residential_address_extras
							?.answer
					}
				/>

				<AnswerField
					hasValue={
						!!experience.expertise.answers.residential_state?.answer
					}
					labelTitle={t('experience_shell:state_territory')}
					value={
						experience.expertise.answers.residential_state?.answer
					}
				/>
				<AnswerField
					hasValue={
						!!experience.expertise.answers.residential_zip?.answer
					}
					labelTitle={t('experience_shell:zip_code')}
					value={experience.expertise.answers.residential_zip?.answer}
				/>
			</Content>

			<Separator orientation='horizontal' />

			<Content margin={6}>
				<YesNoAnswerField
					labelTitle={t('experience_shell:hosting_as_business')}
					yesNoValue={
						experience.expertise.answers?.hosting_as_business
							?.answer
					}
				/>
			</Content>
		</div>
	)
})

BackgroundStep.displayName = 'BackgroundStep'
